import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, Trash2, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Upload as UploadType } from '@/types'
import { formatDistanceToNow } from 'date-fns'
import { sv } from 'date-fns/locale'

interface FileSelectSubStepProps {
  existingUploads: UploadType[]
  isLoading: boolean
  onFileSelect: (file: File) => void
  onDeleteUpload: (uploadId: string) => void
  onContinue: () => void
  canContinue: boolean
}

export function FileSelectSubStep({
  existingUploads,
  isLoading,
  onFileSelect,
  onDeleteUpload,
  onContinue,
  canContinue
}: FileSelectSubStepProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [uploadToDelete, setUploadToDelete] = useState<UploadType | null>(null)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      onFileSelect(file)
    }
  }, [onFileSelect])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    disabled: isLoading
  })

  const handleDeleteClick = (upload: UploadType) => {
    setUploadToDelete(upload)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (uploadToDelete) {
      onDeleteUpload(uploadToDelete.id)
    }
    setDeleteDialogOpen(false)
    setUploadToDelete(null)
  }

  const getPlatformLabel = (platform: string) => {
    const labels: Record<string, string> = {
      bolt: 'Bolt',
      uber: 'Uber',
      sumup: 'SumUp',
      other: 'Annan'
    }
    return labels[platform] || platform
  }

  return (
    <div className="space-y-6">
      {/* Existing uploads */}
      {existingUploads.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Uppladdade filer</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plattform</TableHead>
                  <TableHead>Fil</TableHead>
                  <TableHead className="text-right">Rader</TableHead>
                  <TableHead>Uppladdad</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {existingUploads.map((upload) => (
                  <TableRow key={upload.id}>
                    <TableCell className="font-medium">
                      {getPlatformLabel(upload.platform)}
                    </TableCell>
                    <TableCell className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      {upload.fileName}
                    </TableCell>
                    <TableCell className="text-right">{upload.rowCount}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDistanceToNow(upload.uploadedAt, {
                        addSuffix: true,
                        locale: sv
                      })}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(upload)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* File upload zone */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {existingUploads.length > 0 ? 'Lägg till fler filer' : 'Ladda upp intäktsfiler'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              isDragActive && 'border-primary bg-primary/5',
              isLoading && 'border-muted-foreground/25 bg-muted/50 cursor-not-allowed',
              !isDragActive && !isLoading && 'border-muted-foreground/25 hover:border-muted-foreground/50'
            )}
          >
            <input {...getInputProps()} />

            {isLoading ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <Loader2 className="h-12 w-12 text-muted-foreground animate-spin" />
                </div>
                <div>
                  <p className="font-medium">Bearbetar fil...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <Upload className="h-12 w-12 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">
                    {isDragActive ? 'Släpp filen här' : 'Dra och släpp fil här'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    eller klicka för att välja fil
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    CSV, XLS, XLSX (max 10MB)
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Continue button */}
      {canContinue && (
        <div className="flex justify-end">
          <Button onClick={onContinue}>
            Fortsätt till justeringar
          </Button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort uppladdning?</AlertDialogTitle>
            <AlertDialogDescription>
              Detta tar bort filen &quot;{uploadToDelete?.fileName}&quot; och alla
              inkomster som skapades från den. Denna åtgärd kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
